import styled from 'styled-components';
import { enhancedTextContrast, enhancedHeadingContrast } from '../../../styles/contrastUtils';

export const ModalContainer = styled.div`
  width: 350px;
  max-width: 80vw;
  max-height: 80vh;
  overflow-y: auto; /* Permet le défilement vertical */
  overflow-x: hidden; /* Supprime la barre de défilement horizontale */
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  padding: 16px;
  /* Bordure uniforme tout autour de la modale avec largeur fixe */
  border: 2px solid #4CAF50; /* Bordure plus fine et uniforme */
  margin: 0 auto; /* Centre horizontalement dans le conteneur parent */
  
  /* Style de la scrollbar du modal principal - très discrète */
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(76, 175, 80, 0.3);
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    width: 80vw;
    padding: 12px;
  }
`;

export const ModalHeader = styled.div`
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin: 0;
    font-size: 0.9rem;
    ${enhancedHeadingContrast} /* Utiliser notre utilitaire pour garantir le contraste */
  }
`;

export const TabsContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 480px) {
    gap: 0.5rem;
    padding: 0 0.5rem;
  }
`;

export const Tab = styled.button<{ active: boolean }>`
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.text};
  border-bottom: 2px solid ${({ active, theme }) => active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  white-space: nowrap;
  font-size: 0.9rem;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ContentContainer = styled.div`
  padding: 1rem;
  overflow: visible; /* Permet de voir tout le contenu */
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FormSection = styled.div`
  margin-bottom: 14px;
`;

export const ContactSection = styled.div`
  margin-bottom: 16px;
  /* Match FormSection styling for consistent alignment */
`;

export const ContactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  
  /* Conteneur grisé identique à ActionInputContainer */
  background-color: ${({ theme }) => theme.colors.backgroundHover || '#f8f9fa'};
  border: 1px solid ${({ theme }) => theme.colors.border || '#e9ecef'};
  border-radius: 8px;
  padding: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 12px;
    padding: 16px; /* Un peu plus de padding sur mobile */
  }
`;

export const ContactRow = styled.div`
  display: flex;
  gap: 6px;
  width: 100%;
  margin-bottom: 4px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
  }
`;

export const ContactField = styled.div`
  position: relative;
  margin-bottom: 3px;
  flex: 1;
  
  .field-icon {
    position: absolute;
    left: 7px;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.primaryLight};
    z-index: 1;
    font-size: 12px;
  }
  
  input {
    padding-left: 24px;
    font-size: 0.75rem;
    height: 26px;
    width: 100%;
  }
  
  label {
    margin-bottom: 2px;
    font-size: 0.65rem;
    color: ${({ theme }) => theme.colors.textLight};
  }
  
  @media (max-width: 768px) {
    margin-bottom: 3px;
    
    .field-icon {
      left: 7px;
      font-size: 12px;
    }
    
    input {
      padding-left: 24px;
      font-size: 0.75rem;
      height: 26px;
      border-radius: 4px;
    }
    
    label {
      margin-bottom: 2px;
      font-size: 0.65rem;
    }
  }
`;


export const Input = styled.input`
  height: 28px;
  padding: 0.2rem 0.4rem;
  font-size: 0.85rem;
`;

export const Select = styled.select`
  height: 32px;
  padding: 0.25rem 0.5rem;
  font-size: 0.9rem;
`;

export const FormRow = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 0.25rem;
  
  > * {
    flex: 1;
    min-width: 0;
  }
`;

export const AddressDisplay = styled.div`
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  text-align: center;
  word-break: break-word;
  ${enhancedTextContrast} /* Utiliser notre utilitaire pour garantir le contraste */
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export const CategorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-bottom: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  max-height: 120px;
  overflow-y: auto;

  /* Style de la scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
`;

export const ActionItem = styled.div<{ $isUserAction?: boolean; $isHighlighted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
  min-height: 22px;
  background-color: ${({ $isUserAction }) => 
    $isUserAction ? 'transparent' : 'rgba(233, 236, 239, 0.9)'};
  border-radius: 4px;
  margin-bottom: 0.125rem;
  overflow: hidden;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-left: ${({ $isHighlighted }) => 
    $isHighlighted ? '4px solid #ffc107' : '3px solid transparent'};
  transition: all 0.3s ease;

  /* Animation de pulsation pour l'action mise en évidence */
  ${({ $isHighlighted }) => $isHighlighted && `
    animation: highlightAction 1s ease-in-out 5;
    
    @keyframes highlightAction {
      0% { 
        background-color: rgba(255, 193, 7, 0.2);
        transform: scale(1);
        box-shadow: 0 0 0 rgba(255, 193, 7, 0.7);
      }
      50% { 
        background-color: rgba(255, 193, 7, 0.6);
        transform: scale(1.05);
        box-shadow: 0 0 20px rgba(255, 193, 7, 0.8);
      }
      100% { 
        background-color: rgba(255, 193, 7, 0.2);
        transform: scale(1);
        box-shadow: 0 0 0 rgba(255, 193, 7, 0.7);
      }
    }
  `}

  &:last-child {
    border-bottom: none;
  }
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedTextContrast}
  }
`;

export const ActionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  flex-shrink: 0;
  
  svg {
    width: 9px;
    height: 9px;
  }
`;

export const ActionDate = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.7rem;
  display: inline-block;
  line-height: 1.2;
  margin-right: 0.4rem;
  white-space: nowrap;
  flex-shrink: 0;
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedTextContrast}
  }
`;

export const ActionDescription = styled.span<{ $isExpanded?: boolean }>`
  flex: 1;
  white-space: ${({ $isExpanded }) => $isExpanded ? 'normal' : 'nowrap'};
  overflow: hidden;
  text-overflow: ${({ $isExpanded }) => $isExpanded ? 'clip' : 'ellipsis'};
  cursor: pointer;
  word-break: ${({ $isExpanded }) => $isExpanded ? 'break-word' : 'normal'};
  line-height: ${({ $isExpanded }) => $isExpanded ? '1.4' : '1.2'};
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
  }
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedTextContrast}
  }
`;

// Anciens styles tooltip supprimés - remplacés par le système unifié
// Voir: components/UnifiedTooltip.tsx et hooks/useUnifiedTooltip.ts

export const ActionInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
  
  /* Cadre gris subtil pour distinguer le layout */
  background-color: ${({ theme }) => theme.colors.backgroundHover || '#f8f9fa'};
  border: 1px solid ${({ theme }) => theme.colors.border || '#e9ecef'};
  border-radius: 8px;
  padding: 12px;
  
  /* En mode responsive (mobile), 2 lignes distinctes */
  @media (max-width: 768px) {
    gap: 10px;
    
    input {
      height: 32px;
      font-size: 0.85rem;
    }
    
    select {
      height: 32px;
      font-size: 0.85rem;
      padding: 0 0.6rem;
    }
  }
  
  /* En mode desktop, garder 2 lignes aussi pour cohérence */
  @media (min-width: 769px) {
    gap: 8px;
    
    input {
      height: 26px;
      font-size: 0.75rem;
    }
    
    select {
      height: 26px;
      font-size: 0.75rem;
      padding: 0 0.4rem;
    }
  }
`;

export const ActionSecondLineContainer = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  
  /* En mode mobile, adapter les espacements */
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

export const ActionTypeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 160px; /* Élargi de 120px à 160px (+33%) */
  flex-shrink: 0;
`;

export const AddTypeButton = styled.button<{ $isCheck?: boolean }>`
  background: ${({ theme, $isCheck }) => 
    $isCheck ? theme.colors.successLight : theme.colors.background};
  border: 1px solid ${({ theme, $isCheck }) => 
    $isCheck ? theme.colors.success : theme.colors.border};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: 4px;
  font-size: 12px;
  width: 22px;
  height: 22px;
  transition: all 0.2s ease;
  
  svg {
    color: ${({ theme, $isCheck }) => 
      $isCheck ? theme.colors.success : theme.colors.textSecondary} !important;
  }
  
  &:hover {
    background: ${({ theme, $isCheck }) => 
      $isCheck ? theme.colors.success : theme.colors.backgroundHover};
    border-color: ${({ theme, $isCheck }) => 
      $isCheck ? theme.colors.successDark : theme.colors.primary};
    
    svg {
      color: ${({ theme, $isCheck }) => 
        $isCheck ? theme.colors.background : theme.colors.primary} !important;
    }
  }
`;

export const SaveActionButton = styled(AddTypeButton)`
  background: ${({ theme, $isCheck }) => 
    $isCheck ? theme.colors.success : theme.colors.primary};
  border-color: transparent;
  width: 22px;
  height: 22px;
  padding: 2px;
  
  svg {
    color: ${({ theme }) => theme.colors.background} !important;
    font-size: 10px;
  }

  &:hover {
    background: ${({ theme, $isCheck }) => 
      $isCheck ? theme.colors.successDark : theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.textSecondary};
  }
`;

export const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 0.75rem 0;
  padding: 0;
`;

export const DatePickerContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  border-radius: 4px;
  padding: 10px;

  /* Limiter la hauteur de la liste des heures pour n'afficher que 3 éléments */
  .react-datepicker__time-list {
    max-height: 75px !important;
    overflow-y: auto !important;
    
    /* Faire défiler automatiquement vers 9h00 au chargement */
    scroll-behavior: smooth;
  }

  /* Style de la scrollbar pour la liste des heures */
  .react-datepicker__time-list::-webkit-scrollbar {
    width: 6px;
  }

  .react-datepicker__time-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .react-datepicker__time-list::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  .react-datepicker__time-list::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

export const DatePickerButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  /* Style des boutons Annuler et Confirmer */
  button {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    
    /* Bouton Annuler (premier bouton) */
    &:first-child {
      background-color: #6c757d;
      color: #ffffff;
      
      &:hover {
        background-color: #5a6268;
      }
    }
    
    /* Bouton Confirmer (dernier bouton) */
    &:last-child {
      background-color: #007bff;
      color: #ffffff;
      
      &:hover {
        background-color: #0056b3;
      }
    }
  }

  /* Mode responsive - boutons légèrement plus grands */
  @media (max-width: 768px) {
    button {
      padding: 6px 10px;
      font-size: 13px;
      font-weight: 600;
      min-width: 70px;
    }
  }
`;

export const ActionSection = styled.div`
  max-height: 200px; /* Hauteur suffisante pour environ 5 lignes d'actions */
  overflow-y: auto; /* Scrollbar verticale uniquement si nécessaire */
  overflow-x: hidden; /* Pas de scrollbar horizontale */
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding: 0.25rem;
  margin-top: 0.5rem;
  
  /* Style de la scrollbar */
  &::-webkit-scrollbar {
    width: 4px; /* Scrollbar plus fine */
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 4px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.primaryDark || '#3d8b40'};
    }
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
  flex-shrink: 0;
`;

export const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textLight};
  opacity: 0.7;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ActionReminderContainer = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
  
  svg {
    font-size: 0.9em;
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

export const ReadIndicator = styled.span<{ $isRead: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-left: 4px;
  background-color: ${({ $isRead }) => ($isRead ? '#4CAF50' : '#F44336')};
  color: white;
  font-size: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ $isRead }) => ($isRead ? '#388E3C' : '#D32F2F')};
  }
`;

/* Conteneur pour la 1ère ligne en responsive : Select + bouton + */
export const ActionFirstRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  
  /* Fixer la largeur pour éviter le décalage du bouton + */
  width: 150px;
  flex-shrink: 0;
  
  /* En mode desktop, cette div n'a pas d'effet (flex normal) */
  @media (min-width: 769px) {
    flex: 1;
    width: auto;
  }
`;

/* Conteneur pour la 2ème ligne en responsive : Input + boutons actions */
export const ActionSecondRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  
  /* En mode desktop, cette div n'a pas d'effet (flex normal) */
  @media (min-width: 769px) {
    flex: 1;
  }
`;

